<template>
  <div>
    <AktOptionsPopup v-if="aktOptionsModalOpen"
                     :dateSelectionEnabled="false"
                     @changed="modalResolve"/>
    <b-modal id="patient-print-modal"
             :title="$t('print_noun')"
             @hide="documentType = null"
             @show="onShow"
             centered>
      <div>{{ $t('choose_document_type_and_its_parameters') }}:</div>
      <div class="mt-4">
        <RadioGroup :options="documentTypes"
                    v-model="documentType"
                    validationId="documentType"/>
      </div>
      <div class="row mt-4">
        <div class="col-md-6 col-12">
          <DateGroup :label="$t('date_from')"
                     :required="true"
                     v-model="from"/>
        </div>
        <div class="col-md-6 col-12">
          <DateGroup :label="$t('date_to')"
                     :required="true"
                     v-model="to"/>
        </div>
      </div>
      <div v-if="documentType === 'akt_deposit'">
        <SelectGroup :options="depositAccountsOptions"
                     :showlabel="true"
                     :null-option="false"
                     v-model="depositAccount"
                     variant="white"
                     :label="$t('deposit_account')">
        </SelectGroup>
      </div>
      <ServiceMultiSelect :label="$t('branch')"
                          :dblclick="false"
                          model="branch"
                          title="title"
                          v-if="documentType === 'akt_period' && branches.length > 1"
                          :dbsearch="false"
                          :multiple="false"
                          :onlyId="true"
                          :prepopulatedOptions="branchOptions"
                          v-model="selectedBranch"/>
      <InputGroup v-if="documentType === 'ndfl'"
                  :label="$t('document_number')"
                  v-model="docNumber"/>
      <CheckboxGroup v-if="['ndfl_statement_xml', 'ndfl'].includes(documentType)"
                     v-model="onlyFiscal"
                     fid="only_fiscal_cheques"
                     :label="$t('only_fiscal_payments')"/>
      <div class="alert alert-danger" v-if="error">{{ error }}</div>
      <template #modal-footer="{ close }">
        <div class="modal-footer-buttons">
          <b-button class="cancel mr-3 d-inline-block align-middle"
                    variant="themed"
                    @click="close()">
            {{ $t('cancel') }}
          </b-button>
          <button :disabled="loading || !documentType"
                  @click="next"
                  class="btn-themed align-middle btn-themed-squared">
            {{ $t('continue') }}
          </button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>

import DateGroup from '@/components/form/DateGroup'
import RadioGroup from "@/components/form/RadioGroup"
import JwtService from "@/services/jwt.service"
import { mapState } from "vuex"
import { countries } from "@/dictionaries/dictionary"
import InputGroup from "@/components/form/InputGroup.vue"
import {createAndOpenNDFLReport} from "@/services/ndfl.helper"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import SelectGroup from "@/components/form/SelectGroup.vue"
import AktOptionsPopup from "@/components/parts/patients/AktOptionsPopup.vue"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect.vue"

export default {
  name: "PatientPrintModal",
  components: {
    ServiceMultiSelect,
    AktOptionsPopup,
    SelectGroup,
    CheckboxGroup,
    InputGroup,
    DateGroup,
    RadioGroup
  },
  data() {
    return {
      error: null,
      loading: false,
      from: null,
      to: null,
      docNumber: null,
      documentType: null,
      selectedBranch: 'all',
      depositAccount: 'all',
      allDocumentTypes: [
        { id: 'ortodentic', title: 'ortodentic_formula'},
        { id: 'ortodentic_ru', title: 'ortodentic_formula'},
        // { id: 'tooth_formula', title: 'tooth_formula'},
        { id: 'disease_history', title: 'disease_history'},
        { id: 'f43ukr', title: 'f43ukr', right: 'form_ukraine' },
        { id: 'f43rus', title: 'f43rus', right: 'form_russia'},
        // { id: 'f43kaz', title: 'f43kaz', right: 'form_kaz'},
        { id: 'f58kaz', title: 'f58kaz', right: 'form_kaz'},
        { id: 'f37u78', title: 'f37u78'},
        { id: 'akt_period', title: 'akt_of_services_for_period'},
        { id: 'ndfl_statement', title: 'ndfl_statement', right: 'ndfl_form' },
        { id: 'ndfl', title: 'ndfl_statement_new', right: 'ndfl_form' },
        { id: 'ndfl_statement_xml', title: 'ndfl_statement_xml', right: 'ndfl_form' },
        { id: 'akt_deposit', title: 'deposit_reconciliation_report', right: 'payments'},
      ],
      onlyFiscal: false,
      aktOptionsModalOpen: false,
      modalResolve: () => {},
    }
  },
  methods: {
    onShow() {
      this.onlyFiscal = false
    },
    async getOptionsForAkt() {
      this.aktOptionsModalOpen = true
      await this.$nextTick()
      this.$bvModal.show('akt-options-popup-modal')
      return new Promise((resolve) => {
        this.modalResolve = resolve
      })
    },
    async next() {
      if(!this.documentType) return
      this.error = null
      let signer = null
      if(this.documentType === 'akt' && (!this.from || !this.to)) {
        this.error = this.$t('dates_not_selected')
        return
      }
      if(this.documentType === 'akt_period') {
        const res = await this.getOptionsForAkt()
        setTimeout(() => {
          this.aktOptionsModalOpen = false
        }, 200)
        if(!res) return
        signer = res.signer
      }
      if(this.documentType === 'ndfl') {
        this.loading = true
        try {
          await createAndOpenNDFLReport(
              this.clinic,
              this.branch,
              this.patient,
              this.from,
              this.to,
              this.docNumber,
              this.onlyFiscal,
              () => {
                this.$store.commit('incrementDataTableCounter')
                this.$bvModal.hide('patient-print-modal')
              }
          )
        } finally {
          this.loading = false
        }
        return
      }
      let link = JwtService.getPrintPrelink(this.clinic)
      let url = link +'/document/'+this.patient.id+'/'+this.documentType+'?t=r&branchId='+this.branch
      if(this.from) url += '&from='+this.from
      if(this.to) url += '&to='+this.to
      if(this.docNumber && this.documentType === 'ndfl') url += '&docId='+this.docNumber
      if(this.documentType === 'akt_deposit') url += '&depositAccount='+this.depositAccount
      if(this.onlyFiscal) url += '&onlyFiscal=1'
      if(this.selectedBranch && this.documentType === 'akt_period') url += '&branchId='+this.selectedBranch
      if(signer) url += `&signer=${signer}`
      window.open(url, '_blank')
    }
  },
  computed: {
    ...mapState({
      patient: state => state.patient.patient,
      clinic: state => state.auth.clinic,
      branch: state => state.auth.branch,
      branches: state => state.auth.branches,
      rights: state => state.auth.rights,
      deposit_accounts: state => state.auth.deposit_accounts,
    }),
    depositAccountsOptions() {
      return [{ id: 'all', title: 'all' }, ...this.deposit_accounts]
    },
    branchOptions() {
      return [{ id: 'all', title: this.$t('all') }, ...this.branches]
    },
    documentTypes() {
      const countryId = Number(this.clinic.country_id)
      const arr = this.allDocumentTypes.filter(item => {
        if(!item.right || this.rights.includes(item.right)) {
          if(countryId === countries.UKRAINE && item.id === 'ortodentic_ru') return;
          if(countryId !== countries.UKRAINE && item.id === 'ortodentic') return;
          if(countryId !== countries.UKRAINE && item.id === 'f43ukr') return;
          if(countryId !== countries.KAZAKHSTAN && item.id === 'f58kaz') return;
          if(countryId !== countries.RUSSIA && item.id === 'ndfl_statement') return;
          if(countryId !== countries.RUSSIA && item.id === 'ndfl_statement_xml') return;
          if(![countries.RUSSIA, countries.KYRGYZSTAN].includes(countryId) && item.id === 'f43rus') return;
          if(![countries.RUSSIA, countries.KYRGYZSTAN].includes(countryId) && item.id === 'f37u78') return;
          return item
        }
      })
      return arr
    },
  },
  watch: {
    from() {
      this.error = null
    },
    to() {
      this.error = null
    },
    documentType() {
      this.error = null
    },
  }
}
</script>
